import React from "react";
import { Minus, Plus, Trash2, Tag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { buildProductUrl } from "../../utils/navigation";
import { getImageWithFallback } from "../../utils/imageUtils";
import type { CartItemProps } from "./types";

const CartItemCard: React.FC<CartItemProps> = ({
  item,
  onQuantityChange,
  onRemove,
  isLoading,
}) => {
  const calculateItemPrice = (cartItem: typeof item) => {
    const basePrice = cartItem.usd_price || 0;
    const discountedPrice = basePrice * (1 - (cartItem.sale_rate || 0));
    return discountedPrice * cartItem.cart_quantity;
  };

  const calculateItemOriginalPrice = (cartItem: typeof item) => {
    const basePrice = cartItem.usd_price || 0;
    return basePrice * cartItem.cart_quantity;
  };

  const navigate = useNavigate();
  const goToProduct = () => navigate(buildProductUrl(item.id, item.name || '', item.brand_name));

  const hasDiscount: boolean = !!(item.sale_rate && item.sale_rate > 0);
  const savedAmount = hasDiscount
    ? calculateItemOriginalPrice(item) - calculateItemPrice(item)
    : 0;

  return (
    <div className="group relative bg-section-card-bg border border-section-border rounded-2xl p-4 sm:p-5 hover:border-primary/30 hover:shadow-md transition-all duration-300">
      {hasDiscount && (
        <div className="absolute top-3 left-3 z-10">
          <div className="bg-primary text-primary-foreground text-xs font-bold px-3 py-1.5 rounded-full shadow-sm flex items-center gap-1">
            <Tag className="w-3 h-3" />
            {Math.round((item.sale_rate ?? 0) * 100)}% OFF
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 w-full">
        <button
          onClick={goToProduct}
          className="relative w-full sm:w-28 sm:h-28 md:w-32 md:h-32 bg-section-surface rounded-xl overflow-hidden shrink-0 shadow-sm group-hover:shadow-md transition-shadow mx-auto sm:mx-0 aspect-square cursor-pointer"
        >
          <img
            src={getImageWithFallback(item.image || "")}
            alt={item.name || "Product"}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const img = e.target as HTMLImageElement;
              img.src = getImageWithFallback("");
            }}
          />
        </button>

        <div className="flex-1 min-w-0 flex flex-col w-full">
          <div className="flex flex-row justify-between items-start mb-2 gap-2">
            <div className="flex-1 min-w-0 pr-2">
              <h3
                onClick={goToProduct}
                className="font-bold text-section-text truncate text-base sm:text-lg mb-1 group-hover:text-primary transition-colors cursor-pointer hover:text-primary"
              >
                {item.name || "Unknown Product"}
              </h3>
              {item.description && (
                <p className="text-sm text-section-muted line-clamp-2 leading-relaxed">
                  {item.description}
                </p>
              )}
              {item.variant_options && Object.keys(item.variant_options).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {Object.entries(item.variant_options).map(([type, value]) => (
                    <span
                      key={type}
                      className="inline-flex items-center gap-1 text-xs font-medium bg-section-surface border border-section-border text-section-text px-2 py-0.5 rounded-full"
                    >
                      <span className="text-section-muted">{type}:</span>
                      {value}
                    </span>
                  ))}
                </div>
              )}
              {item.brand_name && (
                <p className="text-xs text-section-muted mt-1 uppercase tracking-wide">
                  Brand: {item.brand_name}
                </p>
              )}
            </div>
            <button
              onClick={() => onRemove(item.cart_key)}
              disabled={isLoading}
              className="p-2 text-section-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
              title="Remove item"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-auto flex flex-col sm:flex-row items-end sm:items-center justify-between pt-3 border-t border-section-border gap-3 sm:gap-0">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <span className="text-xs text-section-muted font-medium">Qty:</span>
              <div className="flex items-center bg-section-surface border border-section-border rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => onQuantityChange(item.cart_key, Math.max(1, item.cart_quantity - 1))}
                  disabled={isLoading || item.cart_quantity <= 1}
                  className="px-3 py-2 hover:bg-section-border hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all text-section-muted"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-4 py-2 text-base font-bold text-section-text min-w-10 text-center bg-section-card-bg">
                  {item.cart_quantity}
                </span>
                <button
                  onClick={() => onQuantityChange(item.cart_key, item.cart_quantity + 1)}
                  disabled={isLoading || (!item.is_unlimited_stock && item.quantity !== undefined && item.cart_quantity >= item.quantity)}
                  className="px-3 py-2 hover:bg-section-border hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all text-section-muted"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="text-right w-full sm:w-auto">
              {hasDiscount ? (
                <div className="space-y-1">
                  <div className="text-sm text-section-muted line-through font-medium">
                    ${calculateItemOriginalPrice(item).toFixed(2)}
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-section-text">
                    ${calculateItemPrice(item).toFixed(2)}
                  </div>
                  <div className="text-xs text-green-600 font-semibold">
                    You save ${savedAmount.toFixed(2)}
                  </div>
                </div>
              ) : (
                <div className="text-xl sm:text-2xl font-bold text-section-text">
                  ${calculateItemPrice(item).toFixed(2)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!item.is_unlimited_stock && item.quantity !== undefined && item.quantity < 10 && item.quantity > 0 && (
        <div className="mt-3 pt-3 border-t border-section-border">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-orange-500 font-semibold">Only {item.quantity} left in stock</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartItemCard;
